import { Injectable } from '@angular/core';

/**
 * The raw record fields the file/page deep-link resolver needs (Issue #1616).
 *
 * Callers adapt their own row shape — a billing transaction's `metadata.taskCompletion`, a task's
 * `metadata` — into this neutral shape. Everything is optional so a half-populated legacy record
 * degrades to plain text instead of throwing.
 */
export interface FilePageLinkSource {
  /** Project the file belongs to. Without it no route can be built at all. */
  projectId?: string | null;
  /**
   * File identifier exactly as stored on the record: a numeric file ID on modern rows, the
   * original file name on legacy ones.
   */
  rawFileId?: string | null;
  /**
   * The identifier after the caller's own legacy filename → ID resolution (Billing's
   * `fileIdCache`). Defaults to {@link rawFileId} when the caller has nothing extra to add.
   */
  fileIdentifier?: string | null;
  /** Page value exactly as stored on the record (`pageId`, `page_number`, …). */
  rawPageId?: string | number | null;
  /** File name recorded on the row itself, when the writer captured one. */
  filename?: string | null;
  /** File name the caller resolved out of file metadata, when it has one cached. */
  resolvedFileName?: string | null;
  /** Page title recorded on the row itself, when the writer captured one. */
  pageTitle?: string | null;
  /** Page title the caller resolved out of page summaries, when it has one cached. */
  resolvedPageTitle?: string | null;
}

/**
 * The precomputed presentation for one file/page reference.
 *
 * Built once per data change and bound directly by templates, so change detection never rebuilds
 * a `routerLink` array (see {@link FilePageLinkService.buildLinks}).
 */
export interface FilePageLinks {
  /** Display identifier for the File segment (numeric ID, or the legacy file name). */
  fileIdentifier: string;
  /** Normalized page number for the Page segment, or `''` when there is no usable page. */
  pageNumber: string;
  /** Router link for the File segment, or `null` when it must stay plain text. */
  fileDeepLink: string[] | null;
  /** Hover text for the File segment — the real file name whenever it can be resolved. */
  fileTooltip: string;
  /** Accessible name for the File link (WCAG 2.5.3). Starts with "File {identifier}". */
  fileAriaLabel: string;
  /** Router link for the Page segment, or `null` when it must stay plain text. */
  pageDeepLink: string[] | null;
  /** Hover text for the Page segment — the real page title whenever it can be resolved. */
  pageTooltip: string;
  /** Accessible name for the Page link (WCAG 2.5.3). Starts with "Page {pageNumber}". */
  pageAriaLabel: string;
}

/** An empty presentation, used for rows that carry no file/page reference at all. */
const EMPTY_LINKS: Readonly<FilePageLinks> = Object.freeze({
  fileIdentifier: '',
  pageNumber: '',
  fileDeepLink: null,
  fileTooltip: '',
  fileAriaLabel: '',
  pageDeepLink: null,
  pageTooltip: '',
  pageAriaLabel: '',
});

/**
 * Resolves `/projects/:projectId/files/:fileId/pages/:pageNumber/preview` deep links and their
 * hover text for any row that references a file and/or a page.
 *
 * Extracted from `BillingComponent` (Issue #1541) so the Tasks table can render its `Scope` column
 * the same way (Issue #1616). The service is pure: it holds no caches and performs no I/O — each
 * caller keeps its own metadata caches and feeds the resolved names in through
 * {@link FilePageLinkSource}.
 */
@Injectable({ providedIn: 'root' })
export class FilePageLinkService {
  /**
   * Returns the numeric file ID usable in a route, or `null` when there is none.
   *
   * Legacy records store the original file name in the ID field; the caller is expected to have
   * already swapped in a numeric ID via {@link FilePageLinkSource.fileIdentifier} if it could.
   * Anything that still is not all digits cannot be used to build a route.
   */
  resolveNumericFileId(source: FilePageLinkSource): string | null {
    const identifier = this.getFileIdentifier(source);
    return /^\d+$/.test(identifier) ? identifier : null;
  }

  /**
   * Normalizes a raw page value to a page number usable in a route.
   * Returns `null` for missing, non-numeric, or non-positive values.
   */
  resolvePageNumber(source: FilePageLinkSource): string | null {
    const rawPageId = source?.rawPageId;
    if (rawPageId === null || rawPageId === undefined || rawPageId === '') return null;

    const pageNumber = String(rawPageId).trim();
    if (!/^\d+$/.test(pageNumber) || Number(pageNumber) < 1) return null;

    return String(Number(pageNumber));
  }

  /** The identifier to display for the File segment: the caller's resolved one, else the raw one. */
  getFileIdentifier(source: FilePageLinkSource): string {
    const identifier = source?.fileIdentifier ?? source?.rawFileId;
    return identifier ? String(identifier) : '';
  }

  /**
   * Router link for the File segment.
   *
   * Prefers the file's page preview (`/projects/:id/files/:fileId/pages/:page/preview`), using the
   * row's own page when known and page 1 otherwise. Falls back to the project's file list when the
   * numeric file ID cannot be resolved (legacy filename-only records).
   *
   * Returns `null` when the segment must stay plain text — i.e. no project or no file at all.
   */
  getFileDeepLink(source: FilePageLinkSource): string[] | null {
    const projectId = source?.projectId;
    const identifier = this.getFileIdentifier(source);
    if (!projectId || !identifier) return null;

    const fileId = this.resolveNumericFileId(source);
    if (!fileId) {
      // No numeric ID to route to — send the user to the project's file list instead.
      return ['/projects', String(projectId), 'settings', 'files'];
    }

    const pageNumber = this.resolvePageNumber(source) ?? '1';
    return ['/projects', String(projectId), 'files', fileId, 'pages', pageNumber, 'preview'];
  }

  /**
   * Router link for the Page segment.
   *
   * Uses the same `/projects/:id/files/:fileId/pages/:page/preview` scheme as chat page links.
   * Returns `null` (plain text) when project, file, or page cannot be resolved.
   */
  getPageDeepLink(source: FilePageLinkSource): string[] | null {
    const projectId = source?.projectId;
    if (!projectId) return null;

    const fileId = this.resolveNumericFileId(source);
    const pageNumber = this.resolvePageNumber(source);
    if (!fileId || !pageNumber) return null;

    return ['/projects', String(projectId), 'files', fileId, 'pages', pageNumber, 'preview'];
  }

  /**
   * Hover text for the File segment — the real file name whenever it can be resolved.
   *
   * Resolution order (Issue #1541):
   *   1. the file name recorded on the row itself
   *   2. the name the caller resolved from file metadata
   *   3. legacy rows where the file ID *is* the file name
   *   4. `File ID: N` — genuine last resort, only when the file no longer resolves
   */
  getFileTooltip(source: FilePageLinkSource): string {
    if (source?.filename) {
      return `File: ${source.filename}`;
    }

    if (source?.resolvedFileName) {
      return `File: ${source.resolvedFileName}`;
    }

    const identifier = this.getFileIdentifier(source);
    if (identifier.length > 20) {
      return `Filename: ${identifier}`;
    }

    return `File ID: ${identifier}`;
  }

  /**
   * Hover text for the Page segment — the real page title whenever it can be resolved.
   *
   * Resolution order (Issue #1541):
   *   1. the page title recorded on the row itself
   *   2. the title the caller resolved from page summaries
   *   3. `Page {m}` — only when the page has no title at all
   */
  getPageTooltip(source: FilePageLinkSource): string {
    if (source?.pageTitle) {
      return String(source.pageTitle);
    }

    const rawPageId = source?.rawPageId;
    if (rawPageId === null || rawPageId === undefined || rawPageId === '') return '';

    if (source?.resolvedPageTitle) {
      return String(source.resolvedPageTitle);
    }

    return `Page ${rawPageId}`;
  }

  /**
   * Builds an accessible name that starts with the control's own visible text (WCAG 2.5.3 Label in
   * Name, Level A) and appends the hover text only when it adds something.
   *
   * The tooltip alone is not usable as a name: it replaces the visible label rather than extending
   * it, so `File 1` announces as `File: plans.pdf` and a voice user saying "click File 1" has
   * nothing to match. Material's `AriaDescriber` does not attach an `aria-describedby` on these
   * anchors, so the hover text has to ride on the name or be lost from the accessibility tree
   * entirely — hence appending rather than dropping it.
   */
  static accessibleName(visibleText: string, hoverText: string): string {
    const detail = (hoverText || '').replace(/^(File(\s*name)?|Page)(\s+ID)?:\s*/i, '').trim();
    if (!detail || visibleText.toLowerCase().includes(detail.toLowerCase())) {
      return visibleText;
    }
    return `${visibleText}, ${detail}`;
  }

  /**
   * Precomputes the whole presentation for one row.
   *
   * Callers run this once per data change and bind the result, so resolving a link costs one pass
   * per data change rather than one per change-detection cycle — tables re-check on every hover
   * and mousemove, and rebuilding `routerLink` arrays there is what makes them feel sticky.
   */
  buildLinks(source: FilePageLinkSource | null | undefined): FilePageLinks {
    if (!source) return { ...EMPTY_LINKS };

    const fileIdentifier = this.getFileIdentifier(source);
    const rawPage = source.rawPageId !== null && source.rawPageId !== undefined ? String(source.rawPageId).trim() : '';
    const pageNumber = this.resolvePageNumber(source) ?? '';
    const pageDisplay = rawPage || pageNumber;
    const fileTooltip = this.getFileTooltip(source);
    const pageTooltip = this.getPageTooltip(source);

    return {
      fileIdentifier,
      pageNumber,
      fileDeepLink: this.getFileDeepLink(source),
      fileTooltip,
      fileAriaLabel: fileIdentifier ? FilePageLinkService.accessibleName(`File ${fileIdentifier}`, fileTooltip) : '',
      pageDeepLink: this.getPageDeepLink(source),
      pageTooltip,
      pageAriaLabel: pageDisplay ? FilePageLinkService.accessibleName(`Page ${pageDisplay}`, pageTooltip) : '',
    };
  }

  /** An empty presentation for rows with no file/page reference. */
  emptyLinks(): FilePageLinks {
    return { ...EMPTY_LINKS };
  }
}
